import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';

import { bizpost } from '../../ajax';
import { Data } from '../../components/base';
import { ProgressDialog } from '../../components/dialogs';
import { GridPanel } from '../../components/grid';
import { formatDate } from '../../components/renderers/formats';
import { clone, setConfig } from '../../core';

const SERVICE_NAME = 'enrolmentrequest';

type Resolution = 'CREATE' | 'LINK';

class EnrolmentRequests extends Component<any, any> {
  static propTypes: any;
  static defaultProps: any;

  constructor(props: any) {
    super(props);
    this.state = {
      loading: false,
      approving: false,
      data: [],
      recordCount: 0,
      rowsPerPage: 10,
      page: 0,
      order: 'desc',
      orderBy: 'dcd',
      cols: [],
      fwopen: false,
      wizmode: '',
      action: '',
      record: {},
      resolution: 'CREATE' as Resolution,
      matchedMemberId: '',
      reviewNotes: '',
    };
  }

  configuration = () => ({
    service: {
      name: SERVICE_NAME,
      title: 'Pending Enrolment Requests',
      key: 'rid',
      num: false,
      pageMode: 'local',
      fields: [
        'rid', 'trc', 'unm', 'ttl', 'fnm', 'otn', 'lnm', 'nam', 'ddb',
        'ema', 'con', 'rad', 'hmt', 'img', 'dti', 'dnm', 'dfn', 'pno',
        'drl', 'rsi', 'rsn', 'rsc', 'frs', 'dpr', 'dps', 'mid', 'rpl',
        'dcd', 'sts', 'ast', 'stp',
      ],
      params: {
        trc: 'Tracking ID',
        unm: 'Username',
        nam: 'Applicant',
        ema: 'Email',
        con: 'Contact',
        rsi: 'Request Status',
        frs: 'Review Flag',
        sts: 'Status',
      },
      grid: [
        ['nam', 'Applicant'],
        ['unm', 'Username'],
        ['ema', 'Email'],
        ['con', 'Contact'],
        ['rsn', 'Request Status'],
        ['frs', 'Review Reason'],
        ['dcd', 'Submitted'],
      ],
      bar: { pag: true, btn: ['view', 'approve'] },
      menu: {
        mnu: [
          { t: 'Applicant', v: 'nam' },
          { t: 'Username', v: 'unm' },
          { t: 'Email', v: 'ema' },
          { t: 'Contact', v: 'con' },
        ],
        def: 'nam',
      },
      eform: [],
    },
    create: false,
    actions: { view: 'View', approve: 'Approve' },
    panels: { view: 'dialog', approve: 'dialog' },
  });

  getServiceConfig = () => {
    const config = setConfig(this.configuration());
    const serviceConfig = clone(config);
    const widths: Record<string, { width: number; minWidth: number; maxWidth: number }> = {
      nam: { width: 210, minWidth: 170, maxWidth: 320 },
      unm: { width: 150, minWidth: 120, maxWidth: 220 },
      ema: { width: 220, minWidth: 170, maxWidth: 320 },
      con: { width: 150, minWidth: 130, maxWidth: 190 },
      rsn: { width: 170, minWidth: 140, maxWidth: 220 },
      frs: { width: 240, minWidth: 180, maxWidth: 360 },
      dcd: { width: 175, minWidth: 155, maxWidth: 220 },
    };
    serviceConfig.service.cols = (serviceConfig.service.cols || []).map((column: any) => ({
      ...column,
      ...(widths[column.id] || {}),
    }));
    return { serviceConfig, serviceData: serviceConfig.service };
  };

  componentDidMount() {
    this.refresh();
  }

  startLoading = () => this.setState({ loading: true });

  stopLoading = () => this.setState({ loading: false });

  refresh = async () => {
    try {
      this.startLoading();
      const response = await bizpost({
        s: SERVICE_NAME,
        a: 'find',
        sts: 1,
        ast: 1,
        pos: 0,
        plm: 1000,
      });
      const rows = (response?.sd || []).map((record: any) => this.normalizeRecord(record));
      this.setState({
        data: rows,
        recordCount: +(response?.rc || rows.length),
        page: 0,
      });
    }
    catch (error) {
      this.setState({ data: [], recordCount: 0 });
      this.notifyError(error, 'Could not load pending enrolment requests');
    }
    finally {
      this.stopLoading();
    }
  };

  normalizeRecord = (record: any) => {
    const applicant = this.firstValue(record, ['nam', 'fullname', 'membername'])
      || [record?.ttl, record?.fnm, record?.otn, record?.lnm].filter(Boolean).join(' ')
      || 'Unknown applicant';
    const requestStatus = this.firstValue(record, [
      'rsn', 'requeststatusname', 'requeststatus', 'rsc', 'requeststatuscode',
    ]) || 'Pending review';
    const reviewReason = this.firstValue(record, [
      'frs', 'dpr', 'duplicatereason', 'fraudreason',
    ]) || (this.duplicateMatches(record).length > 0 ? 'Possible duplicate' : 'Manual review');

    return {
      ...record,
      nam: applicant,
      rsn: requestStatus,
      frs: reviewReason,
      dcd: this.firstValue(record, ['dcd', 'sbd', 'submittedat', 'created']),
    };
  };

  firstValue = (record: any, fields: string[]) => {
    for (const field of fields) {
      const value = record?.[field];
      if (value !== null && value !== undefined && value !== '') return value;
    }
    return null;
  };

  parseJson = (value: any, fallback: any) => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    }
    catch {
      return fallback;
    }
  };

  duplicateMatches = (record: any): any[] => {
    const value = this.firstValue(record, ['dpm', 'dmt', 'dup', 'duplicatematches']);
    const matches = this.parseJson(value, []);
    return Array.isArray(matches) ? matches : [];
  };

  requestPayload = (record: any) => {
    const value = this.firstValue(record, ['rpl', 'requestpayload']);
    return this.parseJson(value, {});
  };

  showText = (value: any) => {
    if (value === null || value === undefined || value === '') return 'Not provided';
    return String(value);
  };

  showDate = (value: any) => {
    if (!value) return 'Not provided';
    const formatted = formatDate(value);
    return formatted === 'Invalid date' ? this.showText(value) : formatted;
  };

  initials = (name: string) => name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'ER';

  formatStatus = (value: any) => {
    const label = this.showText(value);
    const normalized = label.toLowerCase();
    const color = normalized.includes('pending') || normalized.includes('review') ? 'warning' : 'default';
    return <Chip size="small" label={label} color={color as any} variant="outlined" />;
  };

  formatReviewReason = (value: any, record: any) => {
    const score = this.firstValue(record, ['dps', 'duplicatescore']);
    const reason = this.showText(value);
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap title={reason}>{reason}</Typography>
        {score !== null && (
          <Typography variant="caption" color="text.secondary">Match score: {score}%</Typography>
        )}
      </Box>
    );
  };

  rowActions = (_id: any, record: any) => {
    const status = String(this.firstValue(record, ['rsn', 'rsc', 'requeststatusname']) || '').toLowerCase();
    const isResolved = ['approved', 'rejected', 'completed'].some((value) => status.includes(value))
      || !!this.firstValue(record, ['cmi', 'createdmemberid', 'rva', 'reviewedat']);
    return isResolved ? ['view'] : ['view', 'approve'];
  };

  handleRecord = (selection: any) => () => {
    if (!selection?.item) return;
    const record = selection.item;
    const action = selection.action || 'view';
    this.setState({
      record,
      action: Data.actions?.[action] || action,
      wizmode: action,
      fwopen: true,
      resolution: 'CREATE',
      matchedMemberId: this.firstValue(record, ['mid', 'matchedmemberid']) || '',
      reviewNotes: '',
    });
  };

  handleClose = () => {
    if (this.state.approving) return;
    this.setState({ fwopen: false, wizmode: '', action: '', record: {} });
  };

  handleApprove = async () => {
    const { record, resolution, matchedMemberId, reviewNotes } = this.state;
    if (resolution === 'LINK' && !String(matchedMemberId).trim()) {
      this.props.showNotification(
        'Enter the existing member ID when resolving the request as a duplicate.',
        'warn',
        'Member Required'
      );
      return;
    }
    if (!String(reviewNotes).trim()) {
      this.props.showNotification('Add a short review note before approval.', 'warn', 'Review Note Required');
      return;
    }

    try {
      this.setState({ approving: true });
      const response = await bizpost({
        s: SERVICE_NAME,
        a: 'approve',
        rid: record.rid,
        rso: resolution,
        mid: resolution === 'LINK' ? Number(matchedMemberId) : null,
        rvx: reviewNotes.trim(),
        stp: record.stp,
      });
      const notification = response?.notification;
      const notificationFailed = notification?.success === false;
      this.props.showNotification(
        notificationFailed
          ? notification.message || 'Request approved, but the welcome notification failed.'
          : response?.message || 'The enrolment request was approved successfully.',
        notificationFailed ? 'warn' : 'success',
        notificationFailed ? 'Approved With Warning' : 'Enrolment Approved'
      );
      this.setState({ fwopen: false, wizmode: '', record: {} });
      await this.refresh();
    }
    catch (error) {
      this.notifyError(error, 'Could not approve the enrolment request');
    }
    finally {
      this.setState({ approving: false });
    }
  };

  notifyError = (error: any, fallback: string) => {
    const errorx: any = error || {};
    this.props.showNotification(
      errorx.message || errorx.mesg || fallback,
      errorx.type || 'error',
      errorx.title || 'Enrolment Request Error'
    );
  };

  filterColumns = (rows: any[]) => {
    const { cols } = this.state;
    const selected = Array.isArray(cols) && cols.length > 0 ? cols : rows.map((row: any) => row.id);
    return rows.filter((row: any) => selected.includes(row.id));
  };

  setColumns = (_event: any, cols: any) => this.setState({ cols });

  onPageChange = (_event: any, page: number) => this.setState({ page });

  onRowsPerPageChange = (event: any) => this.setState({ rowsPerPage: Number(event.target.value), page: 0 });

  renderDetail = (label: string, value: any) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>{this.showText(value)}</Typography>
    </Box>
  );

  renderDuplicateMatches = (record: any) => {
    const matches = this.duplicateMatches(record);
    if (matches.length === 0) {
      return <Alert severity="info">No structured duplicate matches were returned with this request.</Alert>;
    }

    return (
      <Stack spacing={1.25}>
        {matches.map((match: any, index: number) => {
          const memberId = this.firstValue(match, ['rid', 'mid', 'memberid']);
          const name = this.firstValue(match, ['nam', 'name', 'mfn', 'fullname']) || `Possible match ${index + 1}`;
          const score = this.firstValue(match, ['score', 'dps', 'duplicatescore']);
          return (
            <Box key={`${memberId || 'match'}-${index}`} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">{name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Member ID: {this.showText(memberId)} · {this.showText(this.firstValue(match, ['ema', 'email', 'con', 'contact']))}
                  </Typography>
                </Box>
                {score !== null && <Chip size="small" color="warning" variant="outlined" label={`${score}% match`} />}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  renderViewDialog = () => {
    const { fwopen, wizmode, record } = this.state;
    if (!fwopen || wizmode !== 'view') return null;

    const payload = this.requestPayload(record);
    const name = record.nam || 'Enrolment applicant';
    const photo = this.firstValue(record, ['img', 'imageurl']) || payload?.img || payload?.imageurl || '';
    const documentUrl = this.firstValue(record, ['drl', 'documenturl']) || payload?.drl || payload?.documenturl;
    const duplicateReason = this.firstValue(record, ['frs', 'dpr', 'duplicatereason']);

    return (
      <Dialog open={fwopen} onClose={this.handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Enrolment Review</Typography>
              <Typography variant="caption" color="text.secondary">Tracking ID: {this.showText(record.trc)}</Typography>
            </Box>
            <IconButton onClick={this.handleClose} size="small" aria-label="Close enrolment details"><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '250px minmax(0, 1fr)' }, gap: 3 }}>
            <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3, alignSelf: 'start', textAlign: 'center', bgcolor: 'background.default' }}>
              <Avatar src={photo} alt={name} sx={{ width: 136, height: 136, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 40 }}>
                {this.initials(name)}
              </Avatar>
              <Typography variant="h6">{name}</Typography>
              <Typography variant="body2" color="text.secondary">@{this.showText(record.unm)}</Typography>
              <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                {this.formatStatus(record.rsn)}
                {this.firstValue(record, ['dps', 'duplicatescore']) !== null && (
                  <Chip size="small" color="warning" label={`${this.firstValue(record, ['dps', 'duplicatescore'])}% duplicate score`} />
                )}
              </Stack>
              {documentUrl && /^https?:\/\//i.test(String(documentUrl)) && (
                <Button component={Link} href={String(documentUrl)} target="_blank" rel="noopener noreferrer" startIcon={<DescriptionOutlinedIcon />} variant="outlined" fullWidth sx={{ mt: 2 }}>
                  View baptism document
                </Button>
              )}
            </Box>

            <Stack spacing={3}>
              <Box>
                <Typography variant="overline" color="primary">Applicant details</Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                  {this.renderDetail('Email', record.ema || payload?.ema)}
                  {this.renderDetail('Contact', record.con || payload?.con)}
                  {this.renderDetail('Date of birth', this.showDate(record.ddb || payload?.ddb))}
                  {this.renderDetail('Residential address', record.rad || payload?.rad)}
                  {this.renderDetail('Hometown', record.hmt || payload?.hmt)}
                  {this.renderDetail('Submitted', this.showDate(record.dcd || record.sbd || record.submittedat))}
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="overline" color="warning.main">Why this request needs review</Typography>
                <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>{this.showText(duplicateReason)}</Alert>
                {this.renderDuplicateMatches(record)}
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={this.handleClose}>Close</Button>
          <Button
            variant="contained"
            startIcon={<HowToRegOutlinedIcon />}
            onClick={() => this.setState({ wizmode: 'approve', resolution: 'CREATE', reviewNotes: '' })}
          >
            Review approval
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderApproveDialog = () => {
    const { fwopen, wizmode, record, resolution, matchedMemberId, reviewNotes, approving } = this.state;
    if (!fwopen || wizmode !== 'approve') return null;

    return (
      <Dialog open={fwopen} onClose={this.handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark' }}><HowToRegOutlinedIcon /></Avatar>
              <Box><Typography variant="h6">Approve Enrolment</Typography><Typography variant="caption" color="text.secondary">{record.nam}</Typography></Box>
            </Stack>
            <IconButton onClick={this.handleClose} disabled={approving} size="small" aria-label="Close approval dialog"><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Approval can create a new member account and send welcome credentials. Confirm that the possible duplicate or fraud concern has been reviewed.
          </Alert>
          <Stack spacing={2.5}>
            <TextField
              select
              fullWidth
              label="Resolution"
              value={resolution}
              onChange={(event) => this.setState({ resolution: event.target.value as Resolution })}
              disabled={approving}
            >
              <MenuItem value="CREATE">Approve and create a new member</MenuItem>
              <MenuItem value="LINK">Resolve as an existing member</MenuItem>
            </TextField>
            {resolution === 'LINK' && (
              <TextField
                fullWidth
                required
                type="number"
                label="Existing member ID"
                value={matchedMemberId}
                onChange={(event) => this.setState({ matchedMemberId: event.target.value })}
                helperText="The enrolment request will be linked to this existing member instead of creating another account."
                disabled={approving}
                slotProps={{ htmlInput: { min: 1 } }}
              />
            )}
            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label="Review notes"
              value={reviewNotes}
              onChange={(event) => this.setState({ reviewNotes: event.target.value })}
              placeholder="State what was checked and why this resolution is appropriate."
              disabled={approving}
            />
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PersonSearchOutlinedIcon color="action" />
                <Box>
                  <Typography variant="subtitle2">{record.nam}</Typography>
                  <Typography variant="caption" color="text.secondary">{record.ema} · {record.con}</Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={this.handleClose} disabled={approving}>Cancel</Button>
          <Button variant="contained" color="success" onClick={this.handleApprove} disabled={approving} startIcon={<HowToRegOutlinedIcon />}>
            {approving ? 'Approving…' : resolution === 'LINK' ? 'Confirm existing member' : 'Approve and create member'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { serviceConfig, serviceData } = this.getServiceConfig();
    const { loading, fwopen, data, recordCount, rowsPerPage, page, order, orderBy } = this.state;
    const gridFormatters = {
      rsn: this.formatStatus,
      frs: this.formatReviewReason,
      dcd: (value: any) => this.showDate(value),
    };

    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        {this.renderViewDialog()}
        {this.renderApproveDialog()}
        {loading && (
          <ProgressDialog
            open={loading}
            loading={loading}
            startLoading={this.startLoading}
            stopLoading={this.stopLoading}
          />
        )}
        {!fwopen && (
          <GridPanel
            title={serviceData.title}
            sconfig={serviceConfig}
            sdata={serviceData}
            rows={serviceData.cols}
            data={data}
            rowsPerPage={rowsPerPage}
            page={page}
            pageMode="local"
            order={order}
            orderBy={orderBy}
            recordCount={recordCount}
            formatters={gridFormatters}
            actions={this.rowActions}
            actionSet={this.props.actionSet}
            rowActions={this.props.rowActions}
            refresh={this.refresh}
            setColumns={this.setColumns}
            filterColumns={this.filterColumns}
            handleSelect={this.handleRecord}
            onAction={this.handleRecord}
            onRowsPerPageChange={this.onRowsPerPageChange}
            onPageChange={this.onPageChange}
          />
        )}
      </Box>
    );
  }
}

EnrolmentRequests.propTypes = {
  service: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  setPage: PropTypes.func,
  setHome: PropTypes.func,
  showNotification: PropTypes.func.isRequired,
};

export default EnrolmentRequests;
